import Vue from 'vue';
import Vuex from 'vuex';
import axiosInstance from 'axios';
import { cloneDeep, extendAll, flow, split, isEmpty, reject, map, startCase, toSafeInteger } from 'lodash/fp';
import cookies from 'js-cookie';
import jwt from 'jsonwebtoken';
import moment from 'moment';

// disable immutability so it's easier to modify state
const extendAllMutate = extendAll.convert({ immutable: false });

Vue.use(Vuex);
const siteUrl = process.env.VUE_APP_SERVER_URL;
const cookieName = process.env.VUE_APP_COOKIE;
const axios = axiosInstance.create({ baseURL: siteUrl });

const initialState = {
	user: {
		id: null,
		first_name: null,
		last_name: null,
		admin: null,
		regions: []
	},
	cookie: {
		expires: null
	},
	logged_in: {
		date: null,
		time: null
	},
};

export default new Vuex.Store({
	state: () => cloneDeep(initialState),
	getters: {
		user: state => state.user,
		admin: state => state.user.admin,
		logged_in: state => state.logged_in,
		breadcrumbs: state => flow(
			split('/'),
			reject(isEmpty),
			map(crumb => ({ text: startCase(crumb), to: `/admin/${crumb === 'admin' ? '' : crumb}` })),
			map(({ text, to }) => {
				if (toSafeInteger(text) > 0) {
					return { text: state.route.name, to };
				}
				return { text, to };
			}),
		)(state.route.fullPath)
	},
	mutations: {
		setState: (state, payload) => {
			state.user = {
				id: payload.sub,
				first_name: payload.first_name,
				last_name: payload.last_name,
				admin: payload.admin,
				regions: payload.regions
			};
			state.logged_in = payload.iat;
		},
		setCookieExpiry: (state, { expires }) => {
			state.cookie.expires = expires;
		},
		reset: (state) => {
			cookies.remove(cookieName);
			extendAllMutate([state, cloneDeep(initialState)]);
		},
	},
	actions: {
		async login({ commit }, loginData) {
			const cookie = cookies.get(cookieName);
			if (!cookie) {
				if (loginData) {
					const { data: { token } } = await axios.post('/login', loginData);

					if (token) {
						axios.defaults.headers.common.Authorization = `Bearer ${token}`;
						const decodedToken = jwt.verify(token, process.env.VUE_APP_JWT_SECRET);
						const expires = moment.unix(decodedToken.exp).toDate();
						const now = moment().unix();
						if (expires > now) {
							cookies.set(cookieName, token, { expires });
							commit('setCookieExpiry', { expires });
							commit('setState', decodedToken);
						}
					}
				}
			} else {
				axios.defaults.headers.common.Authorization = `Bearer ${cookie}`;
				const decodedToken = jwt.verify(cookie, process.env.VUE_APP_JWT_SECRET);
				const expires = moment.unix(decodedToken.exp).toDate();
				const now = moment().unix();
				if (expires > now) {
					commit('setCookieExpiry', { expires });
					commit('setState', decodedToken);
				}
			}
		},
		async logout({ commit }) {
			await axios.post('/logout');
			commit('reset');
		}
	},
});
